// Nouveau service de cartes unifié utilisant le pattern Factory
import { IMapService, ParkingSpot, UserLocation } from './mapProviders/IMapService'
import { ParkingDetail } from './mapProviders/overpassService'
import { MapServiceFactory } from './mapProviders/MapServiceFactory'
import { supabase, ParkedVehicle } from '../lib/supabase'

export class UnifiedMapsService {
  private mapService: IMapService
  private currentMap: any = null
  private markers: any[] = []

  constructor() {
    // Obtenir le service de carte approprié via la factory
    this.mapService = MapServiceFactory.getMapService()
  }

  // Méthodes publiques qui délèguent au service approprié
  async loadGoogleMaps(): Promise<void> {
    return this.mapService.loadMap()
  }

  async loadMap(): Promise<void> {
    return this.mapService.loadMap()
  }

  async initializeMap(element: HTMLElement, center: UserLocation): Promise<any> {
    this.currentMap = await this.mapService.initializeMap(element, center)
    return this.currentMap
  }

  async getCurrentLocation(): Promise<UserLocation & { method?: string }> {
    return this.mapService.getCurrentLocation()
  }

  async requestLocationPermission(): Promise<boolean> {
    return this.mapService.requestLocationPermission()
  }

  getMapBounds(map: any) {
    return this.mapService.getMapBounds(map)
  }

  async searchParkingsInBounds(map: any, userLocation?: UserLocation): Promise<ParkingSpot[]> {
    return this.mapService.searchParkingsInBounds(map, userLocation)
  }

  async searchNearbyParkings(location: UserLocation, radius?: number): Promise<ParkingSpot[]> {
    return this.mapService.searchNearbyParkings(location, radius)
  }

  addMarker(map: any, position: UserLocation, title: string, isUser?: boolean, variant?: 'default' | 'parked' | 'selected'): any {
    const marker = this.mapService.addMarker(map, position, title, isUser, variant)
    this.markers.push(marker)
    return marker
  }

  removeMarker(marker: any): void {
    this.mapService.removeMarker(marker)
    const index = this.markers.indexOf(marker)
    if (index > -1) {
      this.markers.splice(index, 1)
    }
  }

  clearMarkers(markers: any[]): void {
    this.mapService.clearMarkers(markers)
    // Nettoyer notre liste locale aussi
    markers.forEach(marker => {
      const index = this.markers.indexOf(marker)
      if (index > -1) {
        this.markers.splice(index, 1)
      }
    })
  }

  openDirections(destination: ParkingSpot): void {
    this.mapService.openDirections(destination)
  }

  calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    return this.mapService.calculateDistance(lat1, lon1, lat2, lon2)
  }

  // Méthodes spécifiques à la gestion des providers
  getCurrentProvider() {
    return MapServiceFactory.getCurrentProvider()
  }

  getAvailableProviders() {
    return MapServiceFactory.getAvailableProviders()
  }

  switchProvider(provider: any) {
    // Nettoyer l'ancien service
    this.cleanup()
    
    // Obtenir le nouveau service
    this.mapService = MapServiceFactory.switchProvider(provider)
    
    return this.mapService
  }

  isGoogleMapsAvailable(): boolean {
    return MapServiceFactory.isGoogleMapsAvailable()
  }

  async fetchParkingDetail(placeId: string): Promise<ParkingDetail | null> {
    if (typeof (this.mapService as any).fetchParkingDetail === 'function') {
      return (this.mapService as any).fetchParkingDetail(placeId)
    }
    return null
  }

  cleanup(): void {
    // Nettoyer les marqueurs locaux
    if (this.markers.length > 0) {
      this.clearMarkers([...this.markers])
    }
    
    // Nettoyer le service de carte
    this.mapService.cleanup()
    
    this.currentMap = null
    this.markers = []
  }

  async reverseGeocode(lat: number, lng: number): Promise<string> {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`)
      const data = await response.json()
      return data.display_name || `${lat}, ${lng}`
    } catch (error) {
      console.error('Error reverse geocoding:', error)
      return `${lat}, ${lng}`
    }
  }

  async parkVehicle(location: UserLocation, expiresAt?: string): Promise<ParkedVehicle | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      const address = await this.reverseGeocode(location.latitude, location.longitude)

      const { data, error } = await supabase
        .from('parked_vehicles')
        .insert({
          user_id: user.id,
          latitude: location.latitude,
          longitude: location.longitude,
          address: address,
          parked_at: new Date().toISOString(),
          expires_at: expiresAt
        })
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error parking vehicle:', error)
      return null
    }
  }

  async getParkedVehicle(): Promise<ParkedVehicle | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null

      const { data, error } = await supabase
        .from('parked_vehicles')
        .select('*')
        .eq('user_id', user.id)
        .is('ended_at', null)
        .order('parked_at', { ascending: false })
        .limit(1)
        .single()

      if (error && error.code !== 'PGRST116') throw error // PGRST116 is "The result contains 0 rows"
      return data
    } catch (error) {
      // console.error('Error getting parked vehicle:', error)
      return null
    }
  }

  async stopParking(vehicleId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('parked_vehicles')
        .update({ ended_at: new Date().toISOString() })
        .eq('id', vehicleId)

      if (error) throw error
      return true
    } catch (error) {
      console.error('Error stopping parking:', error)
      return false
    }
  }
}

// Export de l'instance par défaut pour compatibilité
export const mapsService = new UnifiedMapsService()

// Export des types pour compatibilité
export type { ParkingSpot, UserLocation }
export type { ParkingDetail }
